import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import FlipButton from 'commons/src/components/buttons/FlipButton';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import DeleteConfirmModal from 'commons/src/components/modals/DeleteConfirmModal';
import { LocationType } from 'commons/src/models/commonTypeScript';
import { SelectionModal, SelectionModalItem } from '../../../components/SelectionModal';
import { paths } from '../../../constants';
import { Store } from '../../../reducers';
import styles from './mqttPublisher.module.scss';
import { MqttPublisherConfig, MqttPublisherFormValues } from './mqttPublisherTypes';

type Props = {
    config: MqttPublisherConfig;
    updateConfig: (clientId: string, values: MqttPublisherFormValues) => Promise<MqttPublisherConfig>;
    deleteConfig: (clientId: string) => Promise<void>;
    updateLoading: boolean;
    deleteLoading: boolean;
};

const MqttPublisherDetailView = ({
    config,
    updateConfig,
    deleteConfig,
    updateLoading,
    deleteLoading,
}: Props): React.ReactElement => {
    const { t: txt } = useTranslation();
    const navigate = useNavigate();
    const locations: LocationType[] = useSelector((state: Store) => state.locations.locations);

    const [selectedLocations, setSelectedLocations] = useState<string[]>(config.locations || []);
    const [active, setActive] = useState(config.active);
    const [showBuildingsModal, setShowBuildingsModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    useEffect(() => {
        setSelectedLocations(config.locations || []);
        setActive(config.active);
    }, [config.locations, config.active]);

    const buildingItems: SelectionModalItem[] = locations.map(loc => ({ id: loc.id, label: loc.name }));
    const allLocationIds = locations.map(loc => loc.id);
    const isSubscribedToAll = selectedLocations.length === 0;
    const effectiveSelectedIds = isSubscribedToAll ? allLocationIds : selectedLocations;

    const handleBuildingsSave = (ids: string[]): void => {
        setSelectedLocations(ids.length === locations.length ? [] : ids);
    };

    const selectedLocationNames = locations.filter(loc => selectedLocations.includes(loc.id)).map(loc => loc.name);
    const buildingsText = isSubscribedToAll
        ? txt('MqttPublisher.AllBuildingsSelected')
        : selectedLocationNames.join(', ');

    const handleSave = async (): Promise<void> => {
        try {
            await updateConfig(config.clientId, {
                name: config.name,
                protocol: config.protocol,
                host: config.host,
                port: config.port,
                authType: config.authType,
                username: config.username ?? undefined,
                subscribedToAll: isSubscribedToAll,
                eventTypes: config.eventTypes,
                sensorUnits: config.sensorUnits,
                active,
                locations: selectedLocations,
            });
        } catch {
            // toast shown by hook
        }
    };

    const handleDelete = async (): Promise<void> => {
        try {
            await deleteConfig(config.clientId);
            navigate(`/${paths.mqttPublisher}`);
        } catch {
            // toast shown by hook
        }
    };

    const isCertificateBased = config.authType === 'CERTIFICATE_BASED';

    return (
        <div>
            <div className="page-wrapper__medium page-wrapper__medium--white">
                <div className="settings-details-container settings-details-container--margin">
                    <div className={styles.readOnlyField}>
                        <div className={styles.readOnlyField__label}>{txt('MqttPublisher.Name')}</div>
                        <div className={styles.readOnlyField__value}>{config.name}</div>
                    </div>
                    <div className={styles.readOnlyField}>
                        <div className={styles.readOnlyField__label}>{txt('MqttPublisher.MqttPort')}</div>
                        <div className={styles.readOnlyField__value}>{config.port}</div>
                    </div>
                    <div className={styles.readOnlyField}>
                        <div className={styles.readOnlyField__label}>{txt('MqttPublisher.HostIp')}</div>
                        <div className={styles.readOnlyField__value}>{config.host}</div>
                    </div>
                    {isCertificateBased ? (
                        <div className={styles.readOnlyField}>
                            <div className={styles.readOnlyField__label}>{txt('MqttPublisher.ClientCertificate')}</div>
                            <div className={styles.readOnlyField__value}>
                                <MaterialIcon name="description" /> {config.name}
                            </div>
                        </div>
                    ) : (
                        <>
                            {config.username && (
                                <div className={styles.readOnlyField}>
                                    <div className={styles.readOnlyField__label}>{txt('MqttPublisher.Username')}</div>
                                    <div className={styles.readOnlyField__value}>{config.username}</div>
                                </div>
                            )}
                            <div className={styles.readOnlyField}>
                                <div className={styles.readOnlyField__label}>{txt('MqttPublisher.Password')}</div>
                                <div className={styles.readOnlyField__value}>••••••••••</div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="page-wrapper__medium page-wrapper__medium--white">
                {showDeleteModal && (
                    <DeleteConfirmModal
                        title="MqttPublisher.DeletePublisher"
                        description={txt('MqttPublisher.DeleteDescription', { name: config.name })}
                        onSubmit={handleDelete}
                        onCancel={(): void => setShowDeleteModal(false)}
                        onSubmitText="Delete"
                        onCancelText="Cancel"
                        loading={deleteLoading}
                    />
                )}
                <SelectionModal
                    isOpen={showBuildingsModal}
                    onClose={(): void => setShowBuildingsModal(false)}
                    items={buildingItems}
                    selectedIds={effectiveSelectedIds}
                    onSave={handleBuildingsSave}
                    searchPosition="top"
                    showColumnActions
                    compactButtons
                    title={txt('MqttPublisher.SelectBuildings')}
                    availableTitle={txt('MqttPublisher.AllBuildings')}
                    selectedTitle={txt('MqttPublisher.SelectedBuildings', { count: 0 }).replace(/\s*\(\d+\)/, '')}
                    emptyMessage={txt('MqttPublisher.NoBuildings')}
                    allSelectedMessage={txt('MqttPublisher.AllBuildingsSelected')}
                    noSelectionMessage={txt('MqttPublisher.NoBuildingsSelected')}
                />
                <div className="settings-details-container settings-details-container--margin">
                    <div className={styles.buildingsSection}>
                        <div className={styles.buildingsSection__label}>{txt('MqttPublisher.Buildings')}</div>
                        <div className={styles.buildingsSection__row}>
                            <span>{buildingsText}</span>
                            <button
                                type="button"
                                className={styles.buildingsSection__editLink}
                                onClick={(): void => setShowBuildingsModal(true)}
                            >
                                <MaterialIcon name="edit" />
                                {selectedLocations.length > 0
                                    ? txt('MqttPublisher.ViewEditBuildings')
                                    : txt('MqttPublisher.AddBuildings')}
                            </button>
                        </div>
                    </div>

                    <div className={styles.readOnlyField}>
                        <div className={styles.readOnlyField__label}>{txt('MqttPublisher.Enable')}</div>
                        <FlipButton
                            id="enable"
                            onClick={(): void => setActive(!active)}
                            leftSelected={active}
                            leftText="On"
                            rightText="Off"
                        />
                    </div>

                    <div className="form__row form__button-container">
                        <div className="form__attr--element centered">
                            <PrimaryButton
                                onClick={(): void => setShowDeleteModal(true)}
                                type="button"
                                title="MqttPublisher.Delete"
                                color="alert"
                                loading={deleteLoading}
                                testAttr="delete-mqtt-publisher"
                            />
                        </div>
                        <div className="form__attr--element centered">
                            <PrimaryButton
                                onClick={handleSave}
                                type="button"
                                title="MqttPublisher.Save"
                                color="primary"
                                loading={updateLoading}
                                testAttr="save-mqtt-publisher"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MqttPublisherDetailView;
